import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect } from "react";

interface CalendarDialogProps {
  children?: React.ReactNode;
  trigger?: React.ReactNode;
  buttonVariant?: "default" | "hero" | "dark-primary" | "footer";
  buttonSize?: "default" | "lg" | "xl" | "header";
  buttonClassName?: string;
  buttonText?: string;
  showArrow?: boolean;
}

export function CalendarDialog({
  children,
  trigger,
  buttonVariant = "default",
  buttonSize = "lg",
  buttonClassName,
  buttonText = "Book a Free Strategy Call",
  showArrow = true,
}: CalendarDialogProps) {
  const getButtonVariant = () => {
    switch (buttonVariant) {
      case "hero":
        return "hero";
      case "dark-primary":
        return "dark-primary";
      case "footer":
        return "dark-primary";
      default:
        return "default";
    }
  };

  useEffect(() => {
    const existingScript = document.querySelector(
      'script[src="https://link.msgsndr.com/js/form_embed.js"]'
    );
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://link.msgsndr.com/js/form_embed.js";
      script.type = "text/javascript";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant={getButtonVariant()}
            size={buttonSize}
            className={cn(
              "group font-semibold transition-all duration-400 btn-premium",
              buttonClassName
            )}
          >
            <Calendar className="w-5 h-5 mr-2 opacity-90" />
            {buttonText}
            {showArrow && (
              <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            )}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="!p-0 sm:max-w-[680px] sm:top-[2%] sm:translate-y-0 sm:h-[96vh] border-border/50 shadow-2xl sm:rounded-2xl">
        <div className="flex flex-col h-full">
          <DialogHeader className="p-5 sm:p-8 pb-4 sm:pb-5 border-b border-border/50 bg-card shrink-0">
            <DialogTitle className="text-xl sm:text-2xl font-heading font-bold text-foreground flex items-center gap-3 pr-10">
              <div className="icon-container w-11 h-11 sm:w-12 sm:h-12 flex-shrink-0">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              </div>
              Book Your Strategy Call
            </DialogTitle>
            <DialogDescription className="text-muted-foreground mt-2 text-base">
              Select a time that works for you and we'll discuss your growth goals.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4 sm:p-8 pt-4">
            {children || (
              <iframe
                src="https://api.leadconnectorhq.com/widget/booking/ZQhqkRCwQpCc97DEDBKg"
                className="w-full border-none block rounded-xl"
                style={{ minHeight: "1000px" }}
                id="ZQhqkRCwQpCc97DEDBKg_modal"
                title="Book a Strategy Call"
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}